import { useEffect, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { styled } from '@mui/material/styles';
import dayjs, { type Dayjs } from 'dayjs';
import { Body1 } from '@/components/@IntranetLibrary/Typography';
import BoxButton from '@/components/@IntranetLibrary/Button/BoxButton';
import DatePicker from '@/components/@IntranetLibrary/DatePicker';
import Select from '@/components/@IntranetLibrary/Select';
import Spinner from '@/components/@IntranetLibrary/Spinner';
import { approvalOutlineSelectSx } from '@/components/Approval/@components/ApprovalFormTable';
import {
  buildGroupSalesContentHtml,
  GroupSalesExpand,
  GroupSalesSnapshot,
  GroupSalesTableView,
} from '@/components/Approval/@components/GroupSales';
import { getGroupSalesDraftSourceUrl } from '@/constant/approval';
import useMyBrandOptions from '@/hooks/approval/useMyBrandOptions';
import useGroupSalesShopOptions from '@/hooks/approval/useGroupSalesShopOptions';
import { useSnackbar } from '@/hooks/global-tools';
import { getNestedDataFetcher } from '@/utils/apis';
import type {
  GroupSalesCandidate,
  GroupSalesDraftSource,
  GroupSalesState,
  GroupSalesSummary,
} from '@/utils/approval/groupSales';
import { FieldBlock, FieldHeader } from './styles';
import type { ApprovalFormValues } from './types';

const DATE_FORMAT = 'YYYY-MM-DD';
const CONTROL_HEIGHT = 44;

const toParam = (date: Dayjs | null) =>
  date?.isValid() ? date.format(DATE_FORMAT) : undefined;

// 전체조회는 여러 매장이 섞여 신청번호만으로는 겹칠 수 있음
const candidateKey = (c: GroupSalesCandidate) =>
  `${c.shopCode ?? ''}|${c.sequence ?? ''}`;

type Props = { isEditMode: boolean };

/**
 * 단체판매 진행(61) — 브랜드·매장·기간으로 단체판매 건 조회 → 1건 선택 → 「확인」으로 내역표 확정.
 * 조건이나 선택을 바꾸면 확정값을 비움 — 화면의 표와 저장되는 건이 어긋난 채 상신되지 않게.
 * 편집은 레거시처럼 건을 바꿀 수 없고, 내역을 다시 불러와 저장 표를 갱신
 */
const GroupSalesField = ({ isEditMode }: Props) => {
  const { t } = useTranslation();
  const snackbar = useSnackbar();
  const { control, setValue, getValues } = useFormContext<ApprovalFormValues>();
  const confirmed = useWatch({ control, name: 'groupSales' });
  const { options: brandOptions } = useMyBrandOptions('A');

  const [brandCode, setBrandCode] = useState('');
  const [shopCode, setShopCode] = useState('');
  const { options: shopOptions } = useGroupSalesShopOptions(
    isEditMode ? '' : brandCode,
  );
  const [fromDate, setFromDate] = useState<Dayjs | null>(() =>
    dayjs().startOf('month'),
  );
  const [toDate, setToDate] = useState<Dayjs | null>(() => dayjs());
  const [candidates, setCandidates] = useState<GroupSalesCandidate[]>([]);
  const [selectedKey, setSelectedKey] = useState('');
  const [summary, setSummary] = useState<GroupSalesSummary | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  // 조건을 바꾸면 늦게 온 이전 조건 응답이 목록·표를 덮지 않게 무효 처리
  const requestIdRef = useRef(0);

  const clearConfirmed = () => {
    setSummary(null);
    if (getValues('groupSales'))
      setValue('groupSales', null, { shouldDirty: true });
  };

  const invalidate = () => {
    requestIdRef.current += 1;
    setIsSearching(false);
    setIsConfirming(false);
    setCandidates([]);
    setSelectedKey('');
    clearConfirmed();
  };

  useEffect(() => {
    if (isEditMode || brandCode || !brandOptions.length) return;
    setBrandCode(brandOptions[0].value);
  }, [isEditMode, brandCode, brandOptions]);

  // 레거시: 매장 목록이 오면 첫 매장을 골라 바로 조회
  useEffect(() => {
    if (isEditMode || !shopOptions.length) return;
    const first = shopOptions[0].value;
    setShopCode(first);
    search('1', { shopCode: first });
  }, [isEditMode, shopOptions]);

  const search = async (
    searchType: '1' | '2',
    override?: { shopCode?: string },
  ) => {
    if (!brandCode) return;
    invalidate();
    const requestId = requestIdRef.current;
    setIsSearching(true);
    try {
      const data = await getNestedDataFetcher<GroupSalesDraftSource>(
        getGroupSalesDraftSourceUrl({
          brandCode,
          searchType,
          ...(searchType === '1'
            ? {
                shopCode: override?.shopCode ?? shopCode,
                fromDate: toParam(fromDate),
                toDate: toParam(toDate),
              }
            : {}),
        }),
      );
      if (requestId !== requestIdRef.current) return;
      const list = (data.display.candidates ?? []).filter(
        (c) => c.sequence && c.shopCode,
      );
      setCandidates(list);
      setSelectedKey(list[0] ? candidateKey(list[0]) : '');
    } catch {
      if (requestId !== requestIdRef.current) return;
      snackbar.showError({ message: t('데이터를 불러오지 못했습니다.') });
    } finally {
      if (requestId === requestIdRef.current) setIsSearching(false);
    }
  };

  const fetchSummary = (target: {
    brandCode: string;
    shopCode: string;
    sequence: string | number;
  }) =>
    getNestedDataFetcher<GroupSalesDraftSource>(
      getGroupSalesDraftSourceUrl(target),
    );

  const handleConfirm = async () => {
    if (isConfirming) return;
    const option = candidates.find((c) => candidateKey(c) === selectedKey);
    if (!option?.sequence || !option.shopCode) return;
    requestIdRef.current += 1;
    const requestId = requestIdRef.current;
    setIsConfirming(true);
    try {
      const data = await fetchSummary({
        brandCode: option.brandCode || brandCode,
        shopCode: option.shopCode,
        sequence: option.sequence,
      });
      if (requestId !== requestIdRef.current) return;
      const loaded = data.display.summary;
      if (!loaded) throw new Error('summary missing');
      setSummary(loaded);
      setValue(
        'subject',
        t('{{brand}} {{shop}}점 단체판매 진행의 건', {
          brand: option.brandNameEn ?? '',
          shop: option.shopName ?? '',
        }),
        { shouldDirty: true },
      );
      setValue(
        'groupSales',
        {
          brandCode: option.brandCode || brandCode,
          brandName: data.attributes.brandName ?? option.brandNameEn ?? '',
          shopCode: option.shopCode,
          sequence: Number(option.sequence),
          content: buildGroupSalesContentHtml(loaded, t),
        },
        { shouldDirty: true },
      );
    } catch {
      if (requestId !== requestIdRef.current) return;
      snackbar.showError({ message: t('데이터를 불러오지 못했습니다.') });
    } finally {
      if (requestId === requestIdRef.current) setIsConfirming(false);
    }
  };

  // 편집: 저장된 건의 지금 내역으로 표·저장 HTML 갱신. 실패하면 저장 표를 그대로 보여줌
  const editTargetKey = isEditMode && confirmed ? confirmed.sequence : null;
  useEffect(() => {
    const saved = getValues('groupSales');
    if (!isEditMode || !saved) return;
    let cancelled = false;
    fetchSummary(saved)
      .then((data) => {
        const loaded = data.display.summary;
        if (cancelled || !loaded) return;
        setSummary(loaded);
        const next: GroupSalesState = {
          ...saved,
          content: buildGroupSalesContentHtml(loaded, t),
        };
        setValue('groupSales', next);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [editTargetKey]);

  const renderTable = () => {
    if (summary)
      return (
        <>
          <GroupSalesTableView summary={summary} t={t} />
          <GroupSalesExpand key={summary.sequence} summary={summary} t={t} />
        </>
      );
    if (confirmed?.content)
      return <GroupSalesSnapshot content={confirmed.content} />;
    return null;
  };

  if (isEditMode) return <FieldBlock>{renderTable()}</FieldBlock>;

  return (
    <FieldBlock>
      <FieldHeader>
        <Body1>{t('단체판매')}</Body1>
      </FieldHeader>
      <ControlRow>
        <Select
          value={brandCode}
          options={brandOptions}
          placeholder={t('브랜드 선택')}
          onChange={(e) => {
            invalidate();
            setShopCode('');
            setBrandCode(String(e.target.value));
          }}
          sx={{ ...approvalOutlineSelectSx, width: 180 }}
        />
        <Select
          value={shopCode}
          options={shopOptions}
          placeholder={t('매장 선택')}
          onChange={(e) => {
            invalidate();
            setShopCode(String(e.target.value));
          }}
          sx={{ ...approvalOutlineSelectSx, width: 200 }}
        />
        <DateRange>
          <DatePicker
            value={fromDate}
            onChange={(v) => {
              invalidate();
              setFromDate(v);
            }}
            format={DATE_FORMAT}
            sx={{ width: 160 }}
          />
          <span>~</span>
          <DatePicker
            value={toDate}
            onChange={(v) => {
              invalidate();
              setToDate(v);
            }}
            format={DATE_FORMAT}
            sx={{ width: 160 }}
          />
        </DateRange>
        <BoxButton
          type="button"
          variant="secondary"
          size="medium"
          disabled={!brandCode || isSearching}
          startIcon={
            isSearching ? <Spinner theme="black" size="16px" /> : undefined
          }
          onClick={() => search('1')}
        >
          {t('조회')}
        </BoxButton>
        <BoxButton
          type="button"
          variant="secondary"
          size="medium"
          disabled={!brandCode || isSearching}
          onClick={() => search('2')}
        >
          {t('전체조회')}
        </BoxButton>
      </ControlRow>
      <ControlRow>
        <Select
          value={selectedKey}
          options={candidates.map((c) => ({
            value: candidateKey(c),
            label: c.label ?? '',
          }))}
          placeholder={t('단체판매 건을 조회해 주세요.')}
          onChange={(e) => {
            requestIdRef.current += 1;
            setIsConfirming(false);
            clearConfirmed();
            setSelectedKey(String(e.target.value));
          }}
          sx={{
            ...approvalOutlineSelectSx,
            flex: 1,
            minWidth: 0,
            height: CONTROL_HEIGHT,
          }}
        />
        <BoxButton
          type="button"
          variant="keyColor"
          size="medium"
          disabled={!selectedKey || isConfirming}
          startIcon={
            isConfirming ? <Spinner theme="white" size="16px" /> : undefined
          }
          onClick={handleConfirm}
        >
          {t('확인')}
        </BoxButton>
      </ControlRow>
      {renderTable()}
    </FieldBlock>
  );
};

const ControlRow = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 8,
  marginBottom: 8,
});

const DateRange = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 4,
});

export default GroupSalesField;
